import { Archive } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { CrewMembershipStatus } from "@/types/crew"

/**
 * OKS-26 — 4-value crew membership status badge (UX Spec V2 §Header
 * strip + §Screen Specifications).
 *
 * **Color-token map** (UX V2 lock — reuses the OKS-25 roster palette
 * tokens `ENW7j` / `gDUhN` / `EARsT`; we render via Tailwind tokens
 * that match the picked Pencil hue, not the raw hex):
 *   - `active`           → green  (ENW7j)
 *   - `pending_charter`  → orange (gDUhN — action-required signal)
 *   - `paused`           → gray   (EARsT)
 *   - `archived`         → gray   (EARsT) + Lucide `Archive` icon + strikethrough
 *
 * **Color is never the sole indicator** — every variant renders the
 * localized text label; `archived` additionally pairs with an icon +
 * strikethrough so users with color-vision deficiencies parse the
 * terminal state correctly.
 *
 * **`data-state` attribute** exposes the prop value verbatim so Vitest
 * + Playwright key off the prop, not derived state.
 *
 * Default testid is `crew-member-status-badge`; consumers in the
 * detail header pass `creator-detail-status-badge` to honor the
 * locked QA contract.
 */

interface StatusVisual {
  className: string
}

const STATUS_VISUAL: Record<CrewMembershipStatus, StatusVisual> = {
  active: {
    className:
      "border-emerald-200 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700/60",
  },
  pending_charter: {
    className:
      "border-amber-200 bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700/60",
  },
  paused: {
    className:
      "border-zinc-200 bg-zinc-100 text-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-200 dark:border-zinc-600/60",
  },
  archived: {
    className:
      "border-zinc-200 bg-zinc-100 text-zinc-700 line-through dark:bg-zinc-800/50 dark:text-zinc-300 dark:border-zinc-600/60",
  },
}

/**
 * Resolve the `crew.statuses.*` i18n label via literal `t()` arguments
 * so the inertia_i18n health scanner can statically prove every key is
 * reachable. Dynamic template strings (`t(\`crew.statuses.${status}\`)`)
 * silently bypass the scanner.
 */
function statusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: CrewMembershipStatus,
): string {
  switch (status) {
    case "active":
      return t("crew.statuses.active")
    case "pending_charter":
      return t("crew.statuses.pending_charter")
    case "paused":
      return t("crew.statuses.paused")
    case "archived":
      return t("crew.statuses.archived")
  }
}

export interface CrewMemberStatusBadgeProps {
  status: CrewMembershipStatus
  className?: string
  /** Override the default `data-testid`. */
  testId?: string
}

export function CrewMemberStatusBadge({
  status,
  className,
  testId = "crew-member-status-badge",
}: CrewMemberStatusBadgeProps) {
  const { t } = useTranslation()
  const visual = STATUS_VISUAL[status]
  const label = statusLabel(t, status)

  return (
    <Badge
      variant="outline"
      role="status"
      aria-label={label}
      data-testid={testId}
      data-state={status}
      className={cn("gap-1.5", visual.className, className)}
    >
      {status === "archived" && (
        <Archive aria-hidden="true" className="size-3.5" />
      )}
      <span>{label}</span>
    </Badge>
  )
}
